import { useCallback, useEffect, useRef, useState } from "react"

import type { GroupedActivityMessage } from "../types/activity-message-types"

const SCROLL_CONTAINER_SELECTOR = "[data-scroll-container]"
const SCROLL_SETUP_DELAY = 300
const NAV_RESET_DELAY = 100
const RETRY_DELAY_MULTIPLIER = 500
const MAX_RETRY_COUNT = 5
const SCROLL_THRESHOLD = 20 // pixels from bottom to consider "at bottom"
const PROGRAMMATIC_FLAG = "__programmaticScrollFlag"

type ProgrammaticFlaggedElement = HTMLElement & { [PROGRAMMATIC_FLAG]?: boolean }

const setProgrammaticFlag = (element: HTMLElement, value: boolean) => {
  ;(element as ProgrammaticFlaggedElement)[PROGRAMMATIC_FLAG] = value
}

const getProgrammaticFlag = (element: HTMLElement): boolean => {
  return Boolean((element as ProgrammaticFlaggedElement)[PROGRAMMATIC_FLAG])
}

type UseActivityMessageScrollProps = {
  isAccordionOpen: boolean
  groupedMessages: GroupedActivityMessage[]
  id?: string
}

type ScrollContainerInfo = {
  container: HTMLElement
  containerRect: DOMRect
}

export function useActivityMessageScroll({
  isAccordionOpen,
  groupedMessages,
}: UseActivityMessageScrollProps) {
  const [activeSection, setActiveSection] = useState<number>(0)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const lastMessageCountRef = useRef(0)
  const lastScrollTopRef = useRef(0)

  const handleActiveChange = useCallback((section: number) => {
    setActiveSection(section)
  }, [])

  // Check if scrolled to bottom
  const isAtBottom = useCallback((container: HTMLElement) => {
    const { scrollTop, scrollHeight, clientHeight } = container
    return scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD
  }, [])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!isAccordionOpen || !scrollContainerRef.current || isUserScrolling) return

    const totalActivities = groupedMessages.reduce((sum, group) => sum + group.activities.length, 0)

    // Check if new activities were added
    if (totalActivities > lastMessageCountRef.current && lastMessageCountRef.current > 0) {
      const container = scrollContainerRef.current
      // Scroll to bottom smoothly
      setTimeout(() => {
        scrollToPosition(container, container.scrollHeight)
      }, 100)
    }

    lastMessageCountRef.current = totalActivities
  }, [groupedMessages, isAccordionOpen, isUserScrolling])

  // Initialize last message count when accordion opens to avoid treating initial mount as a change
  useEffect(() => {
    if (!isAccordionOpen) return

    const totalActivities = groupedMessages.reduce((sum, group) => sum + group.activities.length, 0)
    lastMessageCountRef.current = totalActivities
  }, [isAccordionOpen, groupedMessages])

  // Track user scrolling
  const handleUserScroll = useCallback(() => {
    if (!scrollContainerRef.current) return

    const container = scrollContainerRef.current
    const atBottom = isAtBottom(container)
    const previousTop = lastScrollTopRef.current
    const currentTop = container.scrollTop
    const scrolledUp = currentTop < previousTop - 1

    // If at bottom, always resume auto-scrolling
    if (atBottom) {
      setIsUserScrolling(false)
    } else if (scrolledUp) {
      // Only mark as user scrolling when the user scrolls up
      setIsUserScrolling(true)
    }

    lastScrollTopRef.current = currentTop
  }, [isAtBottom])

  useScrollListener(isAccordionOpen, groupedMessages, {
    onActiveChange: handleActiveChange,
    onUserScroll: handleUserScroll,
    scrollContainerRef,
  })
  useNavigationAutoScroll(activeSection, isAccordionOpen)
  useNavigationReset(isAccordionOpen)

  const scrollToSection = useCallback(
    (groupIndex: number) => {
      const targetElement = findElementById(createElementId("group", groupIndex))
      if (!targetElement) return

      const scrollContainer = findScrollContainer(targetElement)
      if (!scrollContainer) return

      const containerRect = scrollContainer.getBoundingClientRect()
      const targetRect = targetElement.getBoundingClientRect()
      const scrollTop = scrollContainer.scrollTop
      const targetPosition = targetRect.top - containerRect.top + scrollTop

      scrollToPosition(scrollContainer, targetPosition)

      // User initiated scroll, so track their position
      setIsUserScrolling(true)
      setTimeout(() => {
        if (isAtBottom(scrollContainer)) {
          setIsUserScrolling(false)
        }
      }, 1000)
    },
    [isAtBottom]
  )

  return {
    scrollToSection,
    activeSection,
  }
}

const useScrollListener = (
  isAccordionOpen: boolean,
  groupedMessages: GroupedActivityMessage[],
  callbacks: {
    onActiveChange: (section: number) => void
    onUserScroll: () => void
    scrollContainerRef: React.MutableRefObject<HTMLElement | null>
  }
) => {
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!isAccordionOpen) return

    const handleScroll = () => {
      const sections = getAllSectionElements(groupedMessages)
      if (sections.length === 0) return

      const containerInfo = getScrollContainerInfo(sections[0])
      if (!containerInfo) return
      const { container } = containerInfo

      const activeSection = calculateActiveSection(sections, containerInfo)
      callbacks.onActiveChange(activeSection)

      // Track user scrolling (skip initial detection and programmatic scrolls)
      if (initializedRef.current) {
        const isProgrammatic = getProgrammaticFlag(container)
        if (!isProgrammatic) callbacks.onUserScroll()
      } else {
        initializedRef.current = true
      }
    }

    const setupScrollListener = (retryCount = 0): (() => void) | void => {
      const sections = getAllSectionElements(groupedMessages)

      if (sections.length === 0) {
        if (retryCount < MAX_RETRY_COUNT) {
          setTimeout(
            () => setupScrollListener(retryCount + 1),
            (retryCount + 1) * RETRY_DELAY_MULTIPLIER
          )
          return
        }
        return
      }

      const containerInfo = getScrollContainerInfo(sections[0])
      if (!containerInfo) return

      const { container } = containerInfo
      callbacks.scrollContainerRef.current = container
      container.addEventListener("scroll", handleScroll, { passive: true })
      handleScroll()

      return () => container.removeEventListener("scroll", handleScroll)
    }

    const timeoutId = setTimeout(() => setupScrollListener(), SCROLL_SETUP_DELAY)
    return () => clearTimeout(timeoutId)
  }, [isAccordionOpen, groupedMessages, callbacks])
}

const useNavigationAutoScroll = (activeSection: number, isAccordionOpen: boolean) => {
  useEffect(() => {
    if (!isAccordionOpen) return

    const activeNavItem = findElementById(createElementId("nav-item", activeSection))
    if (!activeNavItem) return

    const navContainer = findScrollContainer(activeNavItem)
    if (!navContainer) return

    const viewport = isElementOutsideViewport(activeNavItem, navContainer)
    if (!viewport.isAbove && !viewport.isBelow) return

    const targetPosition = calculateCenteredScrollPosition(activeNavItem, navContainer)
    scrollToPosition(navContainer, targetPosition)
  }, [activeSection, isAccordionOpen])
}

const useNavigationReset = (isAccordionOpen: boolean) => {
  useEffect(() => {
    if (!isAccordionOpen) return

    const timeoutId = setTimeout(() => {
      const firstNavItem = findElementById(createElementId("nav-item", 0))
      if (!firstNavItem) return

      const navContainer = findScrollContainer(firstNavItem)
      if (!navContainer) return

      scrollToPosition(navContainer, 0, "instant")
    }, NAV_RESET_DELAY)

    return () => clearTimeout(timeoutId)
  }, [isAccordionOpen])
}

const createElementId = (prefix: string, index: number): string => `${prefix}-${index}`

const findElementById = (id: string): HTMLElement | null => document.getElementById(id)

const findScrollContainer = (element: HTMLElement): HTMLElement | null =>
  element.closest(SCROLL_CONTAINER_SELECTOR) as HTMLElement | null

const getAllSectionElements = (groupedMessages: GroupedActivityMessage[]): HTMLElement[] => {
  return groupedMessages
    .map((_, index) => findElementById(createElementId("group", index)))
    .filter((element): element is HTMLElement => element !== null)
}

const getScrollContainerInfo = (element: HTMLElement): ScrollContainerInfo | null => {
  const container = findScrollContainer(element)
  if (!container) return null

  return {
    container,
    containerRect: container.getBoundingClientRect(),
  }
}

const calculateActiveSection = (
  sections: HTMLElement[],
  containerInfo: ScrollContainerInfo
): number => {
  const { containerRect, container } = containerInfo
  const containerHeight = containerRect.height
  const containerTop = containerRect.top

  // Check if we're near the bottom of the scroll area
  const { scrollTop, scrollHeight, clientHeight } = container
  const isNearBottom = scrollHeight - scrollTop - clientHeight <= 50

  let activeSection = 0

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i]
    const sectionRect = section.getBoundingClientRect()
    const sectionTop = sectionRect.top - containerTop

    if (sectionTop <= containerHeight / 2) {
      activeSection = i
    } else {
      break
    }
  }

  // If we're near the bottom and there are sections, the last section should be active
  if (isNearBottom && sections.length > 0) {
    activeSection = sections.length - 1
  }

  return activeSection
}

const isElementOutsideViewport = (
  element: HTMLElement,
  container: HTMLElement
): { isAbove: boolean; isBelow: boolean } => {
  const containerRect = container.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()

  return {
    isAbove: elementRect.top < containerRect.top,
    isBelow: elementRect.bottom > containerRect.bottom,
  }
}

const calculateCenteredScrollPosition = (element: HTMLElement, container: HTMLElement): number => {
  const containerRect = container.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()

  const scrollTop = container.scrollTop
  const elementOffsetTop = elementRect.top - containerRect.top + scrollTop
  const containerHeight = containerRect.height
  const elementHeight = elementRect.height

  return elementOffsetTop - containerHeight / 2 + elementHeight / 2
}

const scrollToPosition = (
  container: HTMLElement,
  position: number,
  behavior: ScrollBehavior = "smooth"
): void => {
  // Guard against treating programmatic scrolls as user scrolls
  // Use a short window where scroll events are ignored by user tracking
  // The duration should cover smooth scroll animations
  const GUARD_MS = behavior === "instant" ? 50 : 600

  // Set a transient programmatic flag on the nearest hook instance if available
  // We infer presence by attaching a symbol on the container element
  setProgrammaticFlag(container, true)

  // Perform the scroll
  container.scrollTo({
    top: position,
    behavior,
  })

  // Clear the element-level flag after a brief period
  setTimeout(() => {
    setProgrammaticFlag(container, false)
  }, GUARD_MS)
}
